import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { Trophy, Loader2 } from 'lucide-react';

const Klasemen = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categoryFilter, setCategoryFilter] = useState('U-8');
  const [categoryOptions, setCategoryOptions] = useState(['U-8', 'U-10', 'U-12', 'U-14']);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(searchParams.get('eventId') || '');
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);

  // Load events once on mount
  useEffect(() => {
    const loadEvents = async () => {
      setEventsLoading(true);
      try {
        const res = await apiService.getPublicEvents();
        const list = res.data || [];
        setEvents(list);
        // If no event selected yet, default to first event
        if (!selectedEventId && list.length > 0) {
          setSelectedEventId(list[0].id || '');
        }
      } catch (err) {
        console.error('Error loading events:', err);
      } finally {
        setEventsLoading(false);
      }
    };
    loadEvents();
  }, []);

  // Sync selectedEventId to URL
  useEffect(() => {
    if (selectedEventId) {
      setSearchParams({ eventId: selectedEventId }, { replace: true });
    }
  }, [selectedEventId]);

  useEffect(() => {
    if (!selectedEventId) return;
    loadData();
  }, [categoryFilter, selectedEventId]);

  useEffect(() => {
    if (!selectedEventId) return;

    const loadCategories = async () => {
      try {
        const response = await apiService.getMatches(null, null, selectedEventId);
        const categories = Array.from(new Set((response.data || []).map((m) => m.category).filter(Boolean)));

        if (categories.length > 0) {
          setCategoryOptions(categories);
          if (!categories.includes(categoryFilter)) {
            setCategoryFilter(categories[0]);
          }
        }
      } catch (err) {
        console.error('Error loading category options:', err);
      }
    };

    loadCategories();
  }, [selectedEventId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await apiService.getStandings(categoryFilter, selectedEventId);
      setStandings(response.data || []);
    } catch (error) {
      console.error('Error loading standings:', error);
      setStandings([]);
    } finally {
      setLoading(false);
    }
  };

  if (eventsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Klasemen</h1>

        {/* Event + Category Filter */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8 flex flex-col sm:flex-row gap-6">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Event
            </label>
            <select
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              {events.length === 0 && (
                <option value="">Belum ada event</option>
              )}
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.name || ev.title || ev.id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kategori
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full md:w-48 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              {categoryOptions.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Standings Table */}
        {!selectedEventId ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">
            Pilih event untuk melihat klasemen.
          </div>
        ) : loading ? (
          <div className="bg-white rounded-lg shadow-md p-12 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-green-600" />
          </div>
        ) : standings.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">
            Belum ada data klasemen untuk kategori {categoryFilter} pada event ini.
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-green-600 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Pos</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Tim</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Main</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">M</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">S</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">K</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">GM</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">GK</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">SG</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Poin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {standings.map((team) => (
                    <tr
                      key={team.id}
                      className={`hover:bg-gray-50 transition-colors ${
                        team.position <= 3 ? 'bg-green-50' : ''
                      }`}
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-2">
                          <span className="font-bold text-gray-900">{team.position}</span>
                          {team.position === 1 && (
                            <Trophy className="h-5 w-5 text-yellow-500" />
                          )}
                          {team.position === 2 && (
                            <Trophy className="h-5 w-5 text-gray-400" />
                          )}
                          {team.position === 3 && (
                            <Trophy className="h-5 w-5 text-orange-600" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-semibold text-gray-900">{team.team}</span>
                      </td>
                      <td className="px-4 py-4 text-center text-gray-700">{team.played}</td>
                      <td className="px-4 py-4 text-center text-gray-700">{team.won}</td>
                      <td className="px-4 py-4 text-center text-gray-700">{team.drawn}</td>
                      <td className="px-4 py-4 text-center text-gray-700">{team.lost}</td>
                      <td className="px-4 py-4 text-center text-gray-700">{team.goals_for}</td>
                      <td className="px-4 py-4 text-center text-gray-700">{team.goals_against}</td>
                      <td className="px-4 py-4 text-center">
                        <span className={`font-semibold ${
                          team.goal_difference > 0
                            ? 'text-green-600'
                            : team.goal_difference < 0
                            ? 'text-red-600'
                            : 'text-gray-700'
                        }`}>
                          {team.goal_difference > 0 ? '+' : ''}{team.goal_difference}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="font-bold text-green-600 text-lg">{team.points}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-6 bg-white rounded-lg shadow-md p-6">
          <h3 className="font-semibold text-gray-900 mb-3">Keterangan:</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm text-gray-600">
            <div><span className="font-semibold">M:</span> Menang</div>
            <div><span className="font-semibold">S:</span> Seri</div>
            <div><span className="font-semibold">K:</span> Kalah</div>
            <div><span className="font-semibold">GM:</span> Gol Memasukkan</div>
            <div><span className="font-semibold">GK:</span> Gol Kemasukan</div>
            <div><span className="font-semibold">SG:</span> Selisih Gol</div>
            <div><span className="font-semibold">Poin:</span> Menang=3, Seri=1, Kalah=0</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Klasemen;
